const STORAGE_KEY = "bc.weekly_theme";
const NEXT_WEEK_STORAGE_KEY = "bc.next_weekly_theme";

interface StoredThemeData {
  id: string;
  weekStart: string; // ISO "YYYY-MM-DD" of the Monday when the theme was chosen
}

/** Menus are planned on the French calendar (the backend uses it too). */
export const MENU_TIME_ZONE = "Europe/Paris";

const PARIS_DATE_FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: MENU_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Today's calendar date in Europe/Paris as a UTC-midnight Date, so that
 * date arithmetic (`setUTCDate`) and `toISOString()` never drift across a
 * time-zone boundary. The previous implementation mixed a local `getDay()`
 * with a UTC `toISOString()`, which returned the wrong Monday around
 * midnight (e.g. Monday 00:30 in Paris resolved to the previous week).
 */
function parisToday(now: Date): Date {
  const parts = PARIS_DATE_FMT.formatToParts(now);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);
  return new Date(Date.UTC(get("year"), get("month") - 1, get("day")));
}

/** Day of week in Europe/Paris: 0=Sun … 6=Sat. */
export function getParisWeekday(now: Date = new Date()): number {
  return parisToday(now).getUTCDay();
}

/** Today's ISO date (YYYY-MM-DD) in Europe/Paris. */
export function getParisToday(now: Date = new Date()): string {
  return parisToday(now).toISOString().slice(0, 10);
}

function weekStartFrom(now: Date, offsetWeeks: number): string {
  const d = parisToday(now);
  const day = d.getUTCDay(); // 0=Sun … 6=Sat
  d.setUTCDate(d.getUTCDate() - (day === 0 ? 6 : day - 1) + 7 * offsetWeeks);
  return d.toISOString().slice(0, 10);
}

/** ISO Monday (YYYY-MM-DD) of the current week, in Europe/Paris. */
export function getCurrentWeekStart(now: Date = new Date()): string {
  return weekStartFrom(now, 0);
}

/** ISO Monday (YYYY-MM-DD) of next week, in Europe/Paris. */
export function getNextWeekStart(now: Date = new Date()): string {
  return weekStartFrom(now, 1);
}

/** ISO-8601 week number of an ISO date (YYYY-MM-DD). */
export function getIsoWeekNumber(isoDate: string): number {
  const [y, m, dd] = isoDate.slice(0, 10).split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1, dd));
  // The Thursday of the week decides which ISO year it belongs to.
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart) / 86_400_000 + 1) / 7);
}

/** Shift an ISO date (YYYY-MM-DD) by a number of days. */
export function addDaysIso(isoDate: string, days: number): string {
  const [y, m, dd] = isoDate.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, dd + days)).toISOString().slice(0, 10);
}

/**
 * Read the user's persisted theme preference for the current week.
 * Returns the theme id (including "surprise" for an explicit Surprise-me
 * selection), or null when nothing is stored or the stored week has expired.
 */
export function getThemePreference(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as StoredThemeData;
    if (data.weekStart === getCurrentWeekStart()) return data.id;
    return null; // new week — treat as unset
  } catch {
    // Malformed or old plain-string format — ignore and treat as unset
    return null;
  }
}

/**
 * Persist the user's theme choice for the current week.
 * Pass null to clear the preference (will re-prompt next open).
 */
export function setThemePreference(themeId: string | null): void {
  try {
    if (!themeId) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      const data: StoredThemeData = {
        id: themeId,
        weekStart: getCurrentWeekStart(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
  } catch {
    // localStorage may be unavailable in some contexts
  }
}

/**
 * Read the user's theme preference for next week.
 * Stored against next Monday's date so the preference survives the Monday
 * boundary — when Monday arrives, getNextWeekStart() equals what was stored.
 */
export function getNextWeekThemePreference(): string | null {
  try {
    const raw = localStorage.getItem(NEXT_WEEK_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as StoredThemeData;
    // Valid for either next week (Thu-Sun before) or current week (Mon after rollover)
    if (
      data.weekStart === getNextWeekStart() ||
      data.weekStart === getCurrentWeekStart()
    ) {
      return data.id;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist the user's theme choice for next week.
 * Pass null to clear the preference.
 */
export function setNextWeekThemePreference(themeId: string | null): void {
  try {
    if (!themeId) {
      localStorage.removeItem(NEXT_WEEK_STORAGE_KEY);
    } else {
      const data: StoredThemeData = {
        id: themeId,
        weekStart: getNextWeekStart(),
      };
      localStorage.setItem(NEXT_WEEK_STORAGE_KEY, JSON.stringify(data));
    }
  } catch {
    // localStorage may be unavailable in some contexts
  }
}
